import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Users, Eye } from 'lucide-react';
import { toast } from 'react-toastify';
import apiService from '../../services/api';
import useToast from '../../hooks/useToast';
import '../CardForm.css';
import ReactDOM from 'react-dom';

const RolesList = ({ onView, currentPage = 1, setCurrentPage = () => { }, itemsPerPage = 10, searchTerm = '' }) => {
    const navigate = useNavigate();
    const { toasts, addToast, removeToast, showSuccess, showError, showWarning } = useToast();
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [businessId, setBusinessId] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: null });

    useEffect(() => {
        toast.dismiss(); // Clear any lingering toasts from previous pages
        loadRoles();
    }, []);

    const loadRoles = async () => {
        try {
            setLoading(true);
            const userDetails = await apiService.getUserDetails();
            let bid = userDetails?.data?.business_id || userDetails?.business_id;

            // If business_id is undefined, use a default value (you can change this)
            if (!bid) {
      /* console.warn('Business ID is undefined, using default value 40'); */
                bid = 40; // Default business_id for testing
            }

            setBusinessId(bid);
      /* console.log('Loading roles for business_id:', bid); */
            const response = await apiService.getRoles(bid);
      /* console.log('Roles API response:', response); */

            if (response.success && response.data) {
      /* console.log('Setting roles from response.data:', response.data); */
                setRoles(response.data);
            } else if (Array.isArray(response)) {
      /* console.log('Setting roles from array response:', response); */
                setRoles(response);
            } else if (response.data && Array.isArray(response.data)) {
      /* console.log('Setting roles from response.data array:', response.data); */
                setRoles(response.data);
            } else {
      /* console.log('No roles found in response'); */
                setRoles([]);
            }
        } catch (error) {
      /* console.error('Error loading roles:', error); */
            showError('Failed to load roles: ' + error.message);
            setRoles([]);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (role) => {
        navigate('/roles/add', { state: { role } });
    };

    const handleDelete = async (role) => {
        if (role.is_default && role.display_name !== 'Cashier' && !role.name?.startsWith('Cashier#')) {
            showWarning('Cannot delete default role');
            return;
        }

        if (role.users_count > 0) {
            if (!window.confirm(`This role is assigned to ${role.users_count} user(s). Are you sure you want to delete it?`)) {
                return;
            }
        } else {
            if (!window.confirm(`Are you sure you want to delete the role "${role.display_name || role.name}"?`)) {
                return;
            }
        }

        try {
            const response = await apiService.deleteRole(role.id);
            if (response.success) {
                showSuccess('Role deleted successfully!');
                loadRoles();
            } else {
                showError(response.message || 'Failed to delete role');
            }
        } catch (error) {
      /* console.error('Error deleting role:', error); */
            showError(error.message || 'Failed to delete role');
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedRoles = (rolesToSort) => {
        if (!sortConfig.key) return rolesToSort;

        return [...rolesToSort].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (aValue === null || aValue === undefined) aValue = '';
            if (bValue === null || bValue === undefined) bValue = '';

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (!isNaN(parseFloat(aValue)) && !isNaN(parseFloat(bValue))) {
                aValue = parseFloat(aValue);
                bValue = parseFloat(bValue);
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const toggleDropdown = (roleId, event) => {
        event.stopPropagation();

        if (openDropdownId === roleId) {
            setOpenDropdownId(null);
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const dropdownHeight = 120; // Approximate height of dropdown
        const viewportHeight = window.innerHeight;

        let top = null;
        let bottom = null;

        if (rect.bottom + dropdownHeight <= viewportHeight) {
            top = rect.bottom + window.scrollY;
        } else {
            bottom = viewportHeight - rect.top + window.scrollY;
        }

        setDropdownPosition({
            top,
            bottom,
            left: rect.left + window.scrollX
        });

        setOpenDropdownId(roleId);
    };

    useEffect(() => {
        const handleClickOutside = () => setOpenDropdownId(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Show loading state with uniform UI
    if (loading) {
        return (
            <div className="loading-sales">
                <h3>⏳ Loading Roles...</h3>
                <p>Fetching roles data from server...</p>
            </div>
        );
    }

    const filteredRoles = roles.filter((role) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        const name = (role.display_name || role.name || '').toLowerCase();
        return name.includes(term);
    });

    const sortedRoles = getSortedRoles(filteredRoles);
    const pageVal = currentPage || 1;
    const perPageVal = itemsPerPage || 10;
    const totalItems = sortedRoles.length;
    const totalPages = Math.ceil(totalItems / perPageVal) || 1;
    const startIndex = (pageVal - 1) * perPageVal;
    const paginatedRoles = sortedRoles.slice(startIndex, startIndex + perPageVal);

    return (
        <>
            <div>
                <div className="table-responsive">
                    <table className="products-table">
                    <thead>
                        <tr>
                            <th style={{ width: '110px' }}>Actions</th>
                            {[
                                { key: 'name', label: 'Role Name' },
                                { key: 'permissions', label: 'Permissions' },
                                { key: 'users_count', label: 'Users' },
                                { key: 'is_default', label: 'Type' }
                            ].map(({ key, label }) => (
                                <th key={key} className="sortable" onClick={() => handleSort(key)}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                        {label}
                                        {sortConfig.key === key ? (
                                            sortConfig.direction === 'asc' ? (
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--etpos-primary)' }}>
                                                    <path d="M12 19V5M5 12l7-7 7 7" />
                                                </svg>
                                            ) : (
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--etpos-primary)' }}>
                                                    <path d="M12 5v14M5 12l7 7 7-7" />
                                                </svg>
                                            )
                                        ) : (
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M7 15l5 5 5-5" />
                                                <path d="M7 9l5-5 5 5" />
                                            </svg>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedRoles.length === 0 ? (
                            <tr>
                                <td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                                    No roles found. Create your first role to get started.
                                </td>
                            </tr>
                        ) : (
                            paginatedRoles.map((role) => (
                                <tr key={role.id}>
                                    <td className="actions-cell">
                                        <div className="action-dropdown-container">
                                            <button
                                                className="action-btn-trigger"
                                                onClick={(e) => toggleDropdown(role.id, e)}
                                            >
                                                Actions <span className="arrow-down">▼</span>
                                            </button>
                                            {openDropdownId === role.id && ReactDOM.createPortal(
                                                <div
                                                    className="action-dropdown-menu"
                                                    style={{
                                                        position: 'fixed',
                                                        top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                                        bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                                        left: `${dropdownPosition.left}px`,
                                                        zIndex: 1000
                                                    }}
                                                >
                                                    <button onClick={() => {
                                                        setOpenDropdownId(null);
                                                        onView && onView(role);
                                                    }}>
                                                        👁️ View
                                                    </button>
                                                    {(!role.is_default || role.display_name === 'Cashier' || role.name?.startsWith('Cashier#')) && (
                                                        <button onClick={() => handleEdit(role)}>
                                                            ✏️ Edit
                                                        </button>
                                                    )}
                                                    {(!role.is_default || role.display_name === 'Cashier' || role.name?.startsWith('Cashier#')) && (
                                                        <button onClick={() => handleDelete(role)}>
                                                            🗑️ Delete
                                                        </button>
                                                    )}
                                                </div>,
                                                document.body
                                            )}
                                        </div>
                                    </td>
                                    <td>{role.display_name || role.name}</td>
                                    <td>{role.permissions?.length || 0} permissions</td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
                                            <Users size={16} />
                                            {role.users_count || 0}
                                        </div>
                                    </td>
                                    <td>
                                        {role.is_default ? (
                                            <span style={{
                                                padding: '0.25rem 0.5rem',
                                                backgroundColor: '#dbeafe',
                                                color: '#1e40af',
                                                borderRadius: '4px',
                                                fontSize: '0.75rem',
                                                fontWeight: '500'
                                            }}>
                                                Default
                                            </span>
                                        ) : (
                                            <span style={{
                                                padding: '0.25rem 0.5rem',
                                                backgroundColor: '#f1f5f9',
                                                color: '#475569',
                                                borderRadius: '4px',
                                                fontSize: '0.75rem',
                                                fontWeight: '500'
                                            }}>
                                                Custom
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

                {totalItems > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', marginTop: '0.5rem', borderTop: '1px solid #e2e8f0' }}>
                        <div style={{ color: '#334155', fontSize: '0.9rem', fontWeight: '500' }}>
                            Showing {startIndex + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                                className="pagination-btn"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', color: '#334155', fontWeight: '500', fontSize: '0.95rem' }}>
                                Page {currentPage} of {totalPages || 1}
                            </span>
                            <button
                                className="pagination-btn"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default RolesList;
